import useModalManager from '@hooks/useModalManager';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMandanten } from '../data/useActiveMandant';
import LedgerDecisionDialog, { LedgerDecision } from '../LedgerDecisionDialog';
import { Mandant } from '../types';

type DocumentDate = string | Date | null | undefined;

/** True for the backend's "please decide" answer, whether handed over as the axios error or as its response. */
export const isLedgerDecisionError = (error: any): boolean => {
    const response = error?.response ?? error;

    return response?.status === 422 && !!response?.data?.errors?.ledger_decision;
};

const startFromError = (error: any): string | null => {
    const response = error?.response ?? error;
    const message = [response?.data?.errors?.ledger_decision ?? []].flat()[0] ?? '';
    const match = /(\d{2})\.(\d{2})\.(\d{4})/.exec(String(message));

    return match ? `${match[3]}-${match[2]}-${match[1]}` : null;
};

/** The Mandant a document books into: its own when known, else the club's oldest, as on the server. */
export const useDocumentMandant = (clubSlug?: string | null, mandantId?: number | null) => {
    const { mandanten } = useMandanten(clubSlug);

    return useMemo(
        () =>
            mandanten.find((m) => m.id === mandantId) ??
            mandanten.reduce<Mandant | undefined>((oldest, m) => (!oldest || m.id < oldest.id ? m : oldest), undefined),
        [mandanten, mandantId]
    );
};

/**
 * Documents dated before the Mandant's accounting start are only booked when the user says so.
 * `resolve` asks up front when the date is known to lie before the start; `retry` asks after the
 * server refused a request for want of a decision. Both answer null when the dialog was closed.
 */
export const useLedgerDecision = (mandant?: Mandant | null) => {
    const { t } = useTranslation('invoice');
    const { addModal, removeModal } = useModalManager();

    const isBeforeStart = useCallback(
        (date: DocumentDate) => {
            if (!mandant?.accounting_enabled || !mandant.accounting_start_date) return false;

            const start = moment(mandant.accounting_start_date, 'YYYY-MM-DD');

            // Until a start in the future is reached the server leaves older documents out without asking.
            if (start.isAfter(moment(), 'day')) return false;

            return moment(date ?? undefined).isBefore(start, 'day');
        },
        [mandant]
    );

    const ask = useCallback(
        (start?: string | null) =>
            new Promise<LedgerDecision | null>((settle) => {
                let settled = false;
                const answer = (decision: LedgerDecision | null) => {
                    if (settled) return;
                    settled = true;
                    settle(decision);
                };

                addModal({
                    title: t('ledgerDecision.title', {
                        date: start ? moment(start, 'YYYY-MM-DD').format('DD.MM.YYYY') : ''
                    }),
                    size: 35,
                    show: true,
                    onClose: () => answer(null),
                    content: (id) => (
                        <LedgerDecisionDialog
                            onDecide={(decision) => {
                                answer(decision);
                                removeModal(id);
                            }}
                        />
                    )
                });
            }),
        [addModal, removeModal, t]
    );

    /** undefined when the document needs no decision, null when the user closed the dialog. */
    const resolve = useCallback(
        async (date: DocumentDate): Promise<LedgerDecision | null | undefined> =>
            isBeforeStart(date) ? ask(mandant?.accounting_start_date) : undefined,
        [ask, isBeforeStart, mandant]
    );

    const retry = useCallback(
        async (error: unknown): Promise<LedgerDecision | null> =>
            isLedgerDecisionError(error) ? ask(startFromError(error) ?? mandant?.accounting_start_date) : null,
        [ask, mandant]
    );

    return useMemo(() => ({ isBeforeStart, ask, resolve, retry }), [ask, isBeforeStart, resolve, retry]);
};

export default useLedgerDecision;
